import type { EquipAction, Flow, Minigame, Achievement, Board, PackItem, Semi, MenuItem, Tip, Case, Floor, FloorIdT, GameConfig, GameEvent, Hazard, Ingredient, Item, Mission, Npc, Pickup, Product, Role, Rules } from './schema';

/** content/ 讀進來的原始資料（瀏覽器用 import.meta.glob、Node 用 fs，兩邊都交給 buildContent 建索引） */
export interface RawContent {
  game: GameConfig; roles: Role[]; items: Item[]; products: Product[]; hazards: Hazard[]; rules: Rules;
  cases: Case[]; floors: Floor[]; npcs: Npc[]; pickups: Pickup[]; achievements: Achievement[]; ingredients: Ingredient[]; boards: Board[]; packItems: PackItem[]; semis: Semi[]; menu: MenuItem[]; tips: Tip[]; flow: Flow; minigames: Minigame[];
  /** v1.1 機台／地點的其他選項 */
  equipActions: { byKind: Record<string, EquipAction[]>; byId: Record<string, EquipAction[]> };
  /** content/missions/**／*.json：type=accident 的是事件，其他是任務 */
  missionFiles: (Mission | GameEvent)[];
}

export interface Content extends Omit<RawContent, 'missionFiles'> {
  missions: Mission[]; events: GameEvent[];
  missionById: Map<string, Mission>; pickupById: Map<string, Pickup>; roleById: Map<string, Role>; productById: Map<string, Product>;
  floorById: Map<FloorIdT, Floor>; npcById: Map<string, Npc>; caseById: Map<string, Case>; boardById: Map<string, Board>; packById: Map<string, PackItem>; semiById: Map<string, Semi>; menuById: Map<string, MenuItem>;
  hazardById: Map<string, Hazard>; itemById: Map<string, Item>; eventById: Map<string, GameEvent>;
  ingredientById: Map<string, Ingredient>; ingredientByName: Map<string, Ingredient>;
  minigameById: Map<string, Minigame>;
}

const byId = <T extends { id: string }>(xs: T[]) => new Map(xs.map(x => [x.id, x]));

export function buildContent(raw: RawContent): Content {
  const { missionFiles, ...rest } = raw;
  const events = missionFiles.filter((m): m is GameEvent => m.type === 'accident').sort((a, b) => a.id.localeCompare(b.id));
  const missions = missionFiles.filter((m): m is Mission => m.type !== 'accident').sort((a, b) => a.id.localeCompare(b.id));
  const cases = [...raw.cases].sort((a, b) => a.id.localeCompare(b.id));
  const floors = [...raw.floors].sort((a, b) => a.order - b.order);
  return {
    ...rest, cases, floors, missions, events,
    missionById: byId(missions), pickupById: byId(raw.pickups), roleById: byId(raw.roles), productById: byId(raw.products),
    floorById: new Map(floors.map(f => [f.id, f])), npcById: byId(raw.npcs), caseById: byId(cases), boardById: byId(raw.boards), packById: byId(raw.packItems), semiById: byId(raw.semis), menuById: byId(raw.menu),
    hazardById: byId(raw.hazards), itemById: byId(raw.items), eventById: byId(events),
    ingredientById: byId(raw.ingredients), ingredientByName: new Map(raw.ingredients.map(i => [i.name, i])),
    minigameById: byId(raw.minigames),
  };
}
